import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Switch,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Menu,
  MenuItem,
  FormControl,
  Select,
  SelectChangeEvent,
  Tabs,
  Tab,
  InputAdornment,
  Divider,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Security as SecurityIcon,
  LockReset as LockResetIcon,
  LocalShipping as LocalShippingIcon,
  Person as PersonIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  DeleteForever as DeleteForeverIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import toast from 'react-hot-toast';
import { colors } from '../utils/constants';

interface AdminUser {
  id: number;
  email: string;
  first_name: string | null;
  last_name: string | null;
  role: string;
  is_active: boolean;
  is_approved: boolean;
  is_temporary_password: boolean;
  created_at: string;
  payment_type: string | null;
  default_invoice_type: string | null;
  send_email_to_trusted_contact: boolean;
  approved_for_next_day_delivery: boolean;
  external_client_id: string | null;
  organization_event_name: string | null;
  is_archived: boolean;
  is_deleted: boolean;
}

interface AdminAddress {
  id: number;
  user_id: number;
  user_email: string;
  user_name: string;
  address_line_1: string;
  address_line_2: string | null;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  phone_number: string | null;
  approved_delivery_fee: number | null;
  approved_delivery_fee_non_standard: number | null;
  created_at: string;
}

const AdminPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAdmin, startImpersonation } = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [addresses, setAddresses] = useState<AdminAddress[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [selectedAddress, setSelectedAddress] = useState<AdminAddress | null>(null);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [deliveryFeeOpen, setDeliveryFeeOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [isTemporary, setIsTemporary] = useState(true);
  const [currentTab, setCurrentTab] = useState(0);
  const [standardFee, setStandardFee] = useState('');
  const [nonStandardFee, setNonStandardFee] = useState('');
  const [externalClientLinkOpen, setExternalClientLinkOpen] = useState(false);
  const [externalClientId, setExternalClientId] = useState('');
  const [userFilter, setUserFilter] = useState<'all' | 'active' | 'archived'>('active');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<AdminUser | null>(null);

  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
      fetchAddresses();
    }
  }, [isAdmin]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/users');
      setUsers(response.data);
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to fetch users';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const fetchAddresses = async () => {
    try {
      const response = await api.get('/admin/addresses');
      setAddresses(response.data);
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to fetch addresses';
      setError(message);
      toast.error(message);
    }
  };

  const handleApprovalToggle = async (user: AdminUser) => {
    try {
      await api.patch(`/admin/users/${user.id}/approve`, {
        is_approved: !user.is_approved
      });
      
      setUsers(users.map(u => 
        u.id === user.id 
          ? { ...u, is_approved: !u.is_approved }
          : u
      ));
      
      toast.success(`User ${!user.is_approved ? 'approved' : 'unapproved'} successfully`);
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to update approval status';
      toast.error(message);
    }
  };

  const handleRoleChange = async (user: AdminUser, newRole: string) => {
    try {
      await api.patch(`/admin/users/${user.id}/role`, {
        role: newRole
      });
      
      setUsers(users.map(u => 
        u.id === user.id 
          ? { ...u, role: newRole }
          : u
      ));
      
      toast.success(`User role updated to ${newRole} successfully`);
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to update user role';
      toast.error(message);
    }
  };

  const handleNextDayDeliveryToggle = async (user: AdminUser) => {
    try {
      await api.patch(`/admin/users/${user.id}/next-day-delivery`, {
        approved_for_next_day_delivery: !user.approved_for_next_day_delivery
      });
      
      setUsers(users.map(u => 
        u.id === user.id 
          ? { ...u, approved_for_next_day_delivery: !u.approved_for_next_day_delivery }
          : u
      ));
      
      toast.success(`User ${!user.approved_for_next_day_delivery ? 'approved' : 'unapproved'} for next-day delivery`);
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to update next-day delivery approval';
      toast.error(message);
    }
  };

  const handlePasswordReset = async () => {
    if (!selectedUser || !newPassword) return;

    try {
      await api.patch(`/admin/users/${selectedUser.id}/reset-password`, {
        new_password: newPassword,
        is_temporary: isTemporary
      });
      
      setUsers(users.map(u => 
        u.id === selectedUser.id 
          ? { ...u, is_temporary_password: isTemporary }
          : u
      ));
      
      toast.success('Password reset successfully');
      setResetPasswordOpen(false);
      setNewPassword('');
      setSelectedUser(null);
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to reset password';
      toast.error(message);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, user: AdminUser) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  const openPasswordReset = () => {
    setResetPasswordOpen(true);
    setAnchorEl(null);
  };

  const openExternalClientLink = () => {
    if (selectedUser) {
      setExternalClientId(selectedUser.external_client_id || '');
    }
    setExternalClientLinkOpen(true);
    // handleMenuClose();
  };

  const handleImpersonateUser = async () => {
    if (!selectedUser) return;

    try {
      await startImpersonation(selectedUser.id);
      handleMenuClose();
      navigate('/');
    } catch (error) {
      console.error('Failed to impersonate user:', error);
    }
  };

  const handleExternalClientLink = async () => {
    if (!selectedUser) {
      return;
    }

    try {
      await api.patch(`/admin/users/${selectedUser.id}/external-client`, {
        external_client_id: externalClientId || null
      });
      
      setUsers(users.map(u => 
        u.id === selectedUser.id 
          ? { ...u, external_client_id: externalClientId || null }
          : u
      ));
      
      const action = externalClientId ? 'linked' : 'unlinked';
      toast.success(`User ${action} to external client successfully`);
      setExternalClientLinkOpen(false);
      setExternalClientId('');
      setSelectedUser(null);
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to update external client link';
      toast.error(message);
    }
  };

  const handleArchiveUser = async (user: AdminUser) => {
    handleMenuClose();
    try {
      const newArchivedState = !user.is_archived;
      await api.patch(`/admin/users/${user.id}/archive`, { is_archived: newArchivedState });
      setUsers(prev => prev.map(u =>
        u.id === user.id ? { ...u, is_archived: newArchivedState } : u
      ));
      toast.success(`User ${newArchivedState ? 'archived' : 'unarchived'} successfully`);
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to update archive status';
      toast.error(message);
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    try {
      await api.patch(`/admin/users/${userToDelete.id}/delete`);
      setUsers(prev => prev.filter(u => u.id !== userToDelete.id));
      toast.success('User deleted successfully');
      setDeleteConfirmOpen(false);
      setUserToDelete(null);
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to delete user';
      toast.error(message);
    }
  };

  const openDeliveryFeeDialog = (address: AdminAddress) => {
    setStandardFee(address.approved_delivery_fee === null ? '' : (address.approved_delivery_fee / 100).toString());
    setNonStandardFee(address.approved_delivery_fee_non_standard === null ? '' : (address.approved_delivery_fee_non_standard / 100).toString());
    setDeliveryFeeOpen(true);
  };

  const handleDeliveryFeeUpdate = async () => {
    if (!selectedAddress) return;

    try {
      const data: any = {};
      if (standardFee) {
        data.approved_delivery_fee = Math.round(parseFloat(standardFee) * 100);
      }
      if (nonStandardFee) {
        data.approved_delivery_fee_non_standard = Math.round(parseFloat(nonStandardFee) * 100);
      }

      await api.patch(`/admin/addresses/${selectedAddress.id}/approve`, data);
      
      setAddresses(addresses.map(addr => 
        addr.id === selectedAddress.id 
          ? { 
              ...addr, 
              approved_delivery_fee: data.approved_delivery_fee || addr.approved_delivery_fee,
              approved_delivery_fee_non_standard: data.approved_delivery_fee_non_standard || addr.approved_delivery_fee_non_standard
            }
          : addr
      ));
      
      toast.success('Delivery fees updated successfully');
      setDeliveryFeeOpen(false);
      setStandardFee('');
      setNonStandardFee('');
      setSelectedAddress(null);
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to update delivery fees';
      toast.error(message);
    }
  };

  if (!isAdmin) {
    return (
      <Container maxWidth={false} disableGutters sx={{ px: { xs: 2, md: 4 } }}>
        <Alert severity="error" sx={{ mt: 2 }}>
          Access denied. This page is only available to administrators.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  const filteredUsers = users.filter(u => {
    if (userFilter === 'active') return !u.is_archived;
    if (userFilter === 'archived') return u.is_archived;
    return true; // 'all'
  });

  return (
    <Container maxWidth={false} disableGutters sx={{ px: { xs: 2, md: 4 } }}>
      <Box sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <SecurityIcon sx={{ mr: 2, color: colors.primary }} />
          <Typography variant="h4" component="h1">
            Admin Management
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Paper elevation={2}>
          <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tab label="Users" />
            <Tab label="Addresses" />
          </Tabs>

          {currentTab === 0 && (
            <Box>
              <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="body2" color="text.secondary">Show:</Typography>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <Select
                    value={userFilter}
                    onChange={(e: SelectChangeEvent) => setUserFilter(e.target.value as 'all' | 'active' | 'archived')}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="archived">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <TableContainer sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Org/Event Name</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Approved</TableCell>
                    <TableCell>Next-Day Delivery</TableCell>
                    <TableCell>Temp Password</TableCell>
                    <TableCell>External Client</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      {user.first_name || user.last_name 
                        ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
                        : '-'
                      }
                    </TableCell>
                    <TableCell>
                      {user.organization_event_name || '-'}
                    </TableCell>
                    <TableCell>
                      <FormControl size="small" sx={{ minWidth: 100 }}>
                        <Select
                          value={user.role}
                          onChange={(e: SelectChangeEvent) => handleRoleChange(user, e.target.value)}
                          disabled={user.role === 'admin'} // Prevent changing admin role through UI
                        >
                          <MenuItem value="user">User</MenuItem>
                          <MenuItem value="admin">Admin</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={user.is_active ? 'Active' : 'Inactive'} 
                        color={user.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={user.is_approved}
                        onChange={() => handleApprovalToggle(user)}
                        color="primary"
                      />
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={user.approved_for_next_day_delivery}
                        onChange={() => handleNextDayDeliveryToggle(user)}
                        color="primary"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={user.is_temporary_password ? 'Yes' : 'No'} 
                        color={user.is_temporary_password ? 'warning' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {user.external_client_id ? (
                        <Chip 
                          label={user.external_client_id} 
                          color="info"
                          size="small"
                        />
                      ) : (
                        <Chip 
                          label="None" 
                          variant="outlined"
                          size="small"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {new Date(user.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <IconButton 
                        onClick={(e) => handleMenuOpen(e, user)}
                        size="small"
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          </Box>
          )}

          {currentTab === 1 && (
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Address</TableCell>
                    <TableCell>City, State</TableCell>
                    <TableCell>Postal Code</TableCell>
                    <TableCell>Standard Fee</TableCell>
                    <TableCell>Non-Standard Fee</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {addresses.map((address) => (
                    <TableRow key={address.id}>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {address.user_name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {address.user_email}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {address.address_line_1}
                          {address.address_line_2 && <>, {address.address_line_2}</>}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {address.city}, {address.state}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {address.postal_code}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {address.approved_delivery_fee !== null ? (
                          <Chip 
                            label={address.approved_delivery_fee === 0 ? 'Free' : `$${(address.approved_delivery_fee / 100).toFixed(2)}`}
                            color={address.approved_delivery_fee === 0 ? 'info' : 'success'}
                            size="small"
                          />
                        ) : (
                          <Chip 
                            label="Not set"
                            color="default"
                            size="small"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {address.approved_delivery_fee_non_standard !== null ? (
                          <Chip 
                            label={address.approved_delivery_fee_non_standard === 0 ? 'Free' : `$${(address.approved_delivery_fee_non_standard / 100).toFixed(2)}`}
                            color={address.approved_delivery_fee_non_standard === 0 ? 'info' : 'success'}
                            size="small"
                          />
                        ) : (
                          <Chip 
                            label="Not set"
                            color="default"
                            size="small"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => openDeliveryFeeDialog(address)}
                          startIcon={<LocalShippingIcon />}
                        >
                          Set Fees
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        {/* Action Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleImpersonateUser}>
            <PersonIcon sx={{ mr: 1 }} />
            Impersonate User
          </MenuItem>
          <MenuItem onClick={openPasswordReset}>
            <LockResetIcon sx={{ mr: 1 }} />
            Reset Password
          </MenuItem>
          <MenuItem onClick={openExternalClientLink}>
            <SecurityIcon sx={{ mr: 1 }} />
            Link External Client
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => selectedUser && handleArchiveUser(selectedUser)}>
            {selectedUser?.is_archived
              ? <><UnarchiveIcon sx={{ mr: 1 }} />Unarchive User</>
              : <><ArchiveIcon sx={{ mr: 1 }} />Archive User</>
            }
          </MenuItem>
          <MenuItem
            onClick={() => {
              setUserToDelete(selectedUser);
              setDeleteConfirmOpen(true);
              setAnchorEl(null);
            }}
            sx={{ color: 'error.main' }}
          >
            <DeleteForeverIcon sx={{ mr: 1 }} />
            Delete User
          </MenuItem>
        </Menu>

        {/* Password Reset Dialog */}
        <Dialog open={resetPasswordOpen} onClose={() => {
          setResetPasswordOpen(false);
          setNewPassword('');
          setSelectedUser(null);
        }}>
          <DialogTitle>
            Reset Password for {selectedUser?.email}
          </DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="New Password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              margin="normal"
              required
              helperText="Password must be at least 6 characters long"
            />
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" gutterBottom>
                Temporary Password Options:
              </Typography>
              <FormControl>
                <Select
                  value={isTemporary ? 'true' : 'false'}
                  onChange={(e) => setIsTemporary(e.target.value === 'true')}
                  size="small"
                >
                  <MenuItem value="true">Temporary (user must change on next login)</MenuItem>
                  <MenuItem value="false">Permanent (user can continue using)</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setResetPasswordOpen(false);
              setNewPassword('');
              setSelectedUser(null);
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handlePasswordReset}
              variant="contained"
              disabled={!newPassword || newPassword.length < 6}
            >
              Reset Password
            </Button>
          </DialogActions>
        </Dialog>

        {/* External Client Link Dialog */}
        <Dialog open={externalClientLinkOpen} onClose={() => {
          setExternalClientLinkOpen(false);
          setSelectedUser(null);
        }} maxWidth="sm" fullWidth>
          <DialogTitle>
            Link External Client for {selectedUser?.email}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Link this user to an external client ID for integration with Sharewares Admin Service.
            </Typography>
            <TextField
              fullWidth
              label="External Client ID"
              value={externalClientId}
              onChange={(e) => setExternalClientId(e.target.value)}
              margin="normal"
              helperText="Enter the external client ID or leave empty to unlink"
              placeholder="e.g., CLIENT-123"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setExternalClientLinkOpen(false);
              setSelectedUser(null);
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleExternalClientLink}
              variant="contained"
            >
              {externalClientId ? 'Link Client' : 'Unlink Client'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delivery Fee Dialog */}
        <Dialog open={deliveryFeeOpen} onClose={() => setDeliveryFeeOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            Set Delivery Fees for {selectedAddress?.user_name}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Address: {selectedAddress?.address_line_1}, {selectedAddress?.city}, {selectedAddress?.state} {selectedAddress?.postal_code}
            </Typography>
            
            <TextField
              fullWidth
              label="Standard Hours Delivery Fee"
              type="number"
              value={standardFee}
              onChange={(e) => setStandardFee(e.target.value)}
              margin="normal"
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              helperText="Mon-Fri, 9am-4pm delivery fee (leave empty to remove approval)"
            />
            
            <TextField
              fullWidth
              label="Non-Standard Hours Delivery Fee"
              type="number"
              value={nonStandardFee}
              onChange={(e) => setNonStandardFee(e.target.value)}
              margin="normal"
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              helperText="Evenings, weekends, and holidays delivery fee (leave empty to remove approval)"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeliveryFeeOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleDeliveryFeeUpdate}
              variant="contained"
              disabled={!standardFee && !nonStandardFee}
            >
              Update Fees
            </Button>
          </DialogActions>
        </Dialog>
        {/* Delete User Confirmation Dialog */}
        <Dialog open={deleteConfirmOpen} onClose={() => {
          setDeleteConfirmOpen(false);
          setUserToDelete(null);
        }}>
          <DialogTitle>Delete User</DialogTitle>
          <DialogContent>
            <Alert severity="error" sx={{ mb: 2 }}>This action cannot be undone.</Alert>
            <Typography>
              Are you sure you want to delete <strong>{userToDelete?.email}</strong>?
              Their email will be prefixed with <strong>"deleted-"</strong> and they will no longer appear in any user list.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setDeleteConfirmOpen(false);
              setUserToDelete(null);
            }}>
              Cancel
            </Button>
            <Button onClick={handleDeleteUser} variant="contained" color="error">
              Delete User
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default AdminPage;